import { useMemo } from "react"

import * as turf from "@turf/turf"

import { sanitizeData } from "@l2r-front/l2r-geodata"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { Source, useMapDispatchContext } from "@l2r-front/l2r-map"
import { PropTypes } from "@l2r-front/l2r-proptypes"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { TRAFFIC_API_VALUES } from "../../constants/traffic"
import { useTraffics } from "../../hooks/queries/useTraffic"

export function TrafficSource(props) {

    const { t } = useTranslation(I18N_NAMESPACE)
    const { setError: setMapError } = useMapDispatchContext()

    const {
        currentActiveLayer,
        ...otherProps
    } = props

    const { data: traffics } = useTraffics({
        as_geojson: true,
    }, {
        onError: () => setMapError(true),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.speedingsSource.error"),
    })

    // Need to copy id into properties because of that mapbox issue:
    // link:https://github.com/mapbox/mapbox-gl-js/issues/2716
    const sanitizedData = useMemo(() => {
        if (!traffics?.features?.length) {
            return null
        }
        const trafficApiValues = TRAFFIC_API_VALUES[currentActiveLayer]
        const trafficFeatures = traffics.features.map((feature) => {
            const positiveValue = feature.properties.traffic_directions?.["+"]?.[trafficApiValues]
            const negativeValue = feature.properties.traffic_directions?.["-"]?.[trafficApiValues]
            const value = (positiveValue >= 0 && negativeValue >= 0) ? positiveValue + negativeValue
                : positiveValue ?? negativeValue ?? -1

            return {
                ...feature,
                properties: {
                    ...feature.properties,
                    [TRAFFIC_API_VALUES[currentActiveLayer]]: value,
                },
            }
        })

        return sanitizeData(turf.featureCollection(trafficFeatures))

    }, [currentActiveLayer, traffics])

    return (
        <Source {...otherProps} type="geojson" data={sanitizedData} tolerance={.001} />
    )
}

TrafficSource.propTypes = Source.propTypes & {
    currentActiveLayer: PropTypes.string,
}